// Centraliza llamadas al backend PHP bajo /api

import { isNativeRuntime } from '../utils/runtimePlatform';

/** Catálogo dotación vehículo (dato; filtrar materiales por material.dotacion_category / requires_vehicle). */
export { M37_CO_GROUPS, M37_DOCUMENT_CO_GROUPS, normalizeM37CoGroup } from '../utils/m37CoRules';

export const VEHICLE_DOTACION_CATEGORIES = [
  { key: 'herramienta', label: 'Herramienta' },
  { key: 'llanta_repuesto', label: 'Llanta / repuesto' },
  { key: 'botiquin', label: 'Botiquín' },
  { key: 'avisos', label: 'Avisos' },
];

const VEHICLE_DOTACION_KEYS = new Set(VEHICLE_DOTACION_CATEGORIES.map((c) => c.key));

export function materialRequiresVehicleFlag(material) {
  if (!material) return false;
  const rv = material.requires_vehicle ?? material.requiresVehicle;
  if (rv === 1 || rv === true) return true;
  const s = String(rv ?? '').trim().toLowerCase();
  return s === '1' || s === 'true';
}

/** Campos de dotación vehículo normalizados desde una fila de materials.php (PDO devuelve TINYINT como string). */
export function normalizeVehicleDotacionFields(row) {
  const cat = String(row?.dotacion_category ?? '').trim();
  return {
    requires_vehicle: materialRequiresVehicleFlag(row) ? 1 : 0,
    dotacion_category: VEHICLE_DOTACION_KEYS.has(cat) ? cat : '',
  };
}

export function isVehicleDotacionMaterial(material) {
  if (!material) return false;
  if (materialRequiresVehicleFlag(material)) return true;
  const cat = String(material.dotacion_category || '').trim();
  return cat !== '' && VEHICLE_DOTACION_KEYS.has(cat);
}

// Determinar la URL base de la API
// Soporta override por REACT_APP_API_ORIGIN/REACT_APP_API_BASE_PATH y mapea localhost:3000 -> XAMPP
const isBrowser = (typeof window !== 'undefined');
const isDevPort = isBrowser && /localhost:(3000|3001|3002|5173|8181|51208)$/.test(window.location.origin);
const isNative = isNativeRuntime();
// Flag para deshabilitar operaciones de escritura en desarrollo
const DISABLE_WRITES = String(process.env.REACT_APP_DISABLE_API_WRITES || '').toLowerCase() === 'true';
const AUTH_TOKEN_KEY = 'senerfi_auth_token';

// Módulo/sección actualmente visible en la app, para que el backend
// (server/api/audit_helper.php) pueda registrar desde dónde se hizo cada
// petición. Antes solo la propia pantalla de auditoría enviaba esta
// cabecera (y encima esa llamada se excluye de su propio registro), así
// que "Origen" quedaba casi siempre vacío. Se actualiza vía
// setCurrentPageContext(), llamado desde el estado de navegación central
// (ver src/pages/Sede.jsx).
let currentPageContext = '';
export function setCurrentPageContext(value) {
  currentPageContext = String(value || '').trim();
}

function normalizeBasePath(input) {
  let out = String(input || '/').trim();
  if (!out) out = '/';
  if (!out.startsWith('/')) out = `/${out}`;
  if (!out.endsWith('/')) out = `${out}/`;
  out = out.replace(/\/{2,}/g, '/');
  return out === '//' ? '/' : out;
}

function readAuthToken() {
  if (typeof window === 'undefined') return null;
  try {
    const s = window.sessionStorage?.getItem(AUTH_TOKEN_KEY);
    if (s) return String(s);
  } catch (_) {}
  try {
    const l = window.localStorage?.getItem(AUTH_TOKEN_KEY);
    if (l) return String(l);
  } catch (_) {}
  return null;
}

function buildAuthHeaderValue() {
  const token = readAuthToken();
  if (!token) return null;
  if (/^\s*Bearer\s+/i.test(token)) return token.trim();
  return `Bearer ${token.trim()}`;
}

// Prioridad: variables de entorno (siempre), luego mapping 3000->XAMPP, luego origen actual
const API_ORIGIN_RAW = (
  process.env.REACT_APP_API_ORIGIN
    || (isDevPort ? 'http://localhost' : (isBrowser ? window.location.origin : ''))
);
const API_ORIGIN = (() => {
  try { return new URL(API_ORIGIN_RAW).origin; } catch (_) {
    return String(API_ORIGIN_RAW).replace(/\/(senerfi|api).*/i, '');
  }
})();
const API_ORIGIN_FINAL = (() => {
  if (isBrowser) {
    if (isNative) {
      const envNative = process.env.REACT_APP_API_ORIGIN;
      return envNative ? String(envNative) : 'https://senerfisas.com';
    }
    if (isDevPort) return API_ORIGIN;
    return window.location.origin;
  }
  return API_ORIGIN;
})();
const isOfflineNative = isNative && /^https:\/\/localhost$/i.test(API_ORIGIN);
const APP_VARIANT = String(process.env.REACT_APP_APP_VARIANT || '').trim().toLowerCase();
const SIG_MOBILE_OFFLINE_ENABLED = isNative && APP_VARIANT === 'sig_mobile';
const OFFLINE_CACHE_KEY = 'senerfi_sig_mobile_offline_cache_v1';
const OFFLINE_QUEUE_KEY = 'senerfi_sig_mobile_offline_queue_v1';
const OFFLINE_EVENT_NAME = 'senerfi:sig-mobile-sync';
const OFFLINE_CACHE_MAX_ITEMS = 120;

function reportSigDebug() {}

function readJsonStorage(key, fallback) {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage?.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch (_) {
    return fallback;
  }
}

function writeJsonStorage(key, value) {
  if (typeof window === 'undefined') return { ok: false, error: null, sizeKb: 0 };
  try {
    const serialized = JSON.stringify(value);
    window.localStorage?.setItem(key, serialized);
    return { ok: true, error: null, sizeKb: Math.round((serialized?.length || 0) / 1024) };
  } catch (err) {
    let sizeKb = 0;
    try { sizeKb = Math.round((JSON.stringify(value)?.length || 0) / 1024); } catch (_) {}
    return { ok: false, error: err, sizeKb };
  }
}

function dispatchOfflineSyncEvent(detail = {}) {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(OFFLINE_EVENT_NAME, { detail }));
  } catch (_) {}
}

function isRuntimeOffline() {
  if (typeof navigator === 'undefined') return false;
  return navigator.onLine === false;
}

function shouldUseSigMobileOffline() {
  return SIG_MOBILE_OFFLINE_ENABLED;
}

function shouldQueueSigMobileMutation(path, method) {
  if (!shouldUseSigMobileOffline()) return false;
  const upperMethod = String(method || 'GET').toUpperCase();
  if (upperMethod === 'GET') return false;
  const endpoint = getEndpointName(path);
  return endpoint.startsWith('sgi_');
}

function getOfflineCacheMap() {
  const cache = readJsonStorage(OFFLINE_CACHE_KEY, {});
  return cache && typeof cache === 'object' ? cache : {};
}

function setOfflineCacheMap(cache) {
  const entries = Object.entries(cache || {}).sort((a, b) => Number(b?.[1]?.savedAt || 0) - Number(a?.[1]?.savedAt || 0));
  writeJsonStorage(OFFLINE_CACHE_KEY, Object.fromEntries(entries.slice(0, OFFLINE_CACHE_MAX_ITEMS)));
}

function saveOfflineCacheEntry(path, payload) {
  if (!shouldUseSigMobileOffline()) return;
  const cache = getOfflineCacheMap();
  cache[path] = {
    payload,
    savedAt: Date.now(),
  };
  setOfflineCacheMap(cache);
}

function getOfflineCacheEntry(path) {
  const cache = getOfflineCacheMap();
  return cache[path]?.payload ?? null;
}

function getOfflineQueue() {
  const queue = readJsonStorage(OFFLINE_QUEUE_KEY, []);
  return Array.isArray(queue) ? queue : [];
}

function setOfflineQueue(queue) {
  const persisted = writeJsonStorage(OFFLINE_QUEUE_KEY, Array.isArray(queue) ? queue : []);
  dispatchOfflineSyncEvent({ pending: Array.isArray(queue) ? queue.length : 0 });
  return persisted;
}

function serializeQueueBody(body) {
  if (body === undefined || body === null) return null;
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    const out = {};
    for (const [key, value] of body.entries()) {
      if (typeof File !== 'undefined' && value instanceof File) return null;
      if (Object.prototype.hasOwnProperty.call(out, key)) {
        const current = out[key];
        out[key] = Array.isArray(current) ? [...current, value] : [current, value];
      } else {
        out[key] = value;
      }
    }
    return out;
  }
  if (typeof body === 'string') return body;
  if (typeof body === 'object') {
    try { return JSON.parse(JSON.stringify(body)); } catch (_) { return null; }
  }
  return body;
}

async function enqueueOfflineMutation({ path, method, body }) {
  const serializableBody = serializeQueueBody(body);
  if (!shouldUseSigMobileOffline() || !serializableBody) {
    const err = new Error('No fue posible guardar localmente esta operación offline.');
    err.code = 'SIG_MOBILE_OFFLINE_UNSUPPORTED';
    throw err;
  }
  const tempId = `offline-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const queue = getOfflineQueue();
  queue.push({
    tempId,
    path,
    method,
    body: serializableBody,
    createdAt: new Date().toISOString(),
  });
  const result = setOfflineQueue(queue);
  if (!result.ok) {
    // localStorage.setItem lanzó (típicamente QuotaExceededError, por el
    // tamaño de fotos/firmas en base64): antes esto se ignoraba en silencio
    // y la app reportaba "guardado" aunque nada quedara realmente en la cola.
    // Se incluye el tipo de error, el tamaño real y (si el navegador lo
    // expone) el uso/cuota real del dispositivo para poder diagnosticar si
    // de verdad es un tema de espacio en el equipo o algo distinto.
    const errName = result.error?.name || result.error?.message || 'error desconocido';
    let storageInfo = '';
    try {
      if (typeof navigator !== 'undefined' && navigator.storage?.estimate) {
        const est = await navigator.storage.estimate();
        const usedMb = ((est.usage || 0) / (1024 * 1024)).toFixed(1);
        const quotaMb = ((est.quota || 0) / (1024 * 1024)).toFixed(1);
        storageInfo = ` Almacenamiento del dispositivo para esta app: ${usedMb} MB usados de ${quotaMb} MB disponibles.`;
      }
    } catch (_) {}
    const err = new Error(`No se pudo guardar en este dispositivo (${errName}, este registro pesa ≈ ${result.sizeKb} KB).${storageInfo} Sincroniza los registros pendientes ("Sincronizar") para liberar espacio, reduce la cantidad de fotos e inténtalo de nuevo.`);
    err.code = 'SIG_MOBILE_OFFLINE_QUOTA_EXCEEDED';
    throw err;
  }
  return tempId;
}

function getPathSearchParams(path) {
  const query = String(path || '').split('?')[1] || '';
  return new URLSearchParams(query);
}

function getEndpointName(path) {
  return String(path || '').split('?')[0].toLowerCase();
}

function isDeleteMutation(item) {
  return String(item?.method || '').toUpperCase() !== 'GET' && String(item?.body?.action || '').toLowerCase() === 'delete';
}

function matchesListFilters(path, body = {}) {
  const params = getPathSearchParams(path);
  const filters = ['location', 'inspection_type', 'document_type', 'poda_type'];
  return filters.every((key) => {
    const expected = String(params.get(key) || '').trim();
    if (!expected) return true;
    return String(body?.[key] || '').trim() === expected;
  });
}

function buildOfflineRow(item) {
  const body = item?.body || {};
  return {
    id: body.id || item.tempId,
    ...body,
    offline_queued: true,
    created_at: item.createdAt,
    updated_at: item.createdAt,
  };
}

function applyOfflineQueueToCachedResponse(path, payload) {
  if (!payload || !Array.isArray(payload?.data)) return payload;
  const endpoint = getEndpointName(path);
  if (!endpoint.startsWith('sgi_')) return payload;
  const rows = Array.isArray(payload.data) ? [...payload.data] : [];
  const queue = getOfflineQueue().filter((item) => getEndpointName(item.path) === endpoint && matchesListFilters(path, item.body));
  let nextRows = rows;
  for (const item of queue) {
    if (isDeleteMutation(item)) {
      nextRows = nextRows.filter((row) => String(row?.id || '') !== String(item?.body?.id || ''));
      continue;
    }
    const offlineRow = buildOfflineRow(item);
    const rowId = String(offlineRow.id || '');
    const idx = nextRows.findIndex((row) => String(row?.id || '') === rowId);
    if (idx >= 0) {
      nextRows[idx] = { ...nextRows[idx], ...offlineRow };
    } else {
      nextRows = [offlineRow, ...nextRows];
    }
  }
  return {
    ...payload,
    data: nextRows,
    offline: true,
  };
}

function getOfflineGetFallback(path) {
  const cached = getOfflineCacheEntry(path);
  if (cached && typeof cached === 'object') {
    return applyOfflineQueueToCachedResponse(path, cached);
  }
  return { ok: true, offline: true, data: [] };
}

function buildOfflineMutationResponse(path, tempId, body) {
  const action = String(body?.action || '').toLowerCase();
  if (action === 'delete') {
    return { ok: true, offline: true, queued: true, id: body?.id || tempId };
  }
  return {
    ok: true,
    offline: true,
    queued: true,
    id: body?.id || tempId,
    data: buildOfflineRow({ tempId, body, createdAt: new Date().toISOString(), path }),
  };
}

const __BASE_OVERRIDE = String(process.env.REACT_APP_API_BASE_PATH || '').trim();
const API_BASE_PATH = (() => {
  try {
    const override = __BASE_OVERRIDE !== '' ? __BASE_OVERRIDE : null;
    const base = String(override || '/');
    const normalized = base.replace(/\/sede\/[^/]+$/i, '');
    return normalizeBasePath(normalized || '/');
  } catch (_) { return '/'; }
})();

let CURRENT_API_BASE_PATH = API_BASE_PATH;
let __validateApiBasePromise = null;

async function ensureApiBaseValidated() {
  if (typeof window === 'undefined') return;
  // En la APK SIG ya conocemos origen/base de la API; evitar sondeos extra que disparan errores CORS en consola.
  if (isNative && __BASE_OVERRIDE !== '' && APP_VARIANT === 'sig_mobile') {
    CURRENT_API_BASE_PATH = API_BASE_PATH;
    // #region debug-point A:skip-base-validation
    reportSigDebug('A', 'Se omite validacion automatica de base API en SIG mobile', {
      currentApiBasePath: CURRENT_API_BASE_PATH,
      apiBasePath: API_BASE_PATH,
      origin: API_ORIGIN_FINAL,
      appVariant: APP_VARIANT,
    });
    // #endregion
    return;
  }
  if (__validateApiBasePromise) return __validateApiBasePromise;
  __validateApiBasePromise = (async () => {
    const inferred = (typeof window !== 'undefined' && /^\/senerfi(\/|$)/i.test(window.location.pathname || ''))
      ? '/senerfi/'
      : '/';
    const candidatesRaw = [
      CURRENT_API_BASE_PATH,
      inferred,
      '/senerfi/',
      '/',
    ];
    const candidates = Array.from(new Set(candidatesRaw.map(normalizeBasePath)));
    for (const base of candidates) {
      const testUrl = `${API_ORIGIN_FINAL}${base}api/status.php`;
      try {
        const res = await fetch(testUrl, { method: 'GET', cache: 'no-store' });
        if (!res || !res.ok) continue;
        let parsed = null;
        try { parsed = await res.json(); } catch (_) { parsed = null; }
        const looksLikeStatus = !!(parsed && typeof parsed === 'object' && (('server_time' in parsed) || ('status' in parsed) || ('ok' in parsed)));
        if (!looksLikeStatus) continue;
        CURRENT_API_BASE_PATH = base;
        return;
      } catch (_) {}
    }
  })();
  return __validateApiBasePromise;
}
// Log de diagnóstico para verificar origen y base en tiempo de ejecución
if (typeof window !== 'undefined') {
  if (!window.__SENERFI_API_LOGGED__) {
    window.__SENERFI_API_LOGGED__ = true;
    console.info('[api] origin:', API_ORIGIN_FINAL, 'baseOverride:', API_BASE_PATH, 'writesDisabled:', DISABLE_WRITES);
  }
}

/**
 * Construye URL completa para endpoint de API
 * @param {string} path - people.php - Ruta del endpoint (ej: 'people.php')
 * @returns {string} URL completa
 */
function buildApiUrl(path) {
  const safePath = path.startsWith('/') ? path.slice(1) : path;
  // La API PHP está en /senerfi/api/
  const base = CURRENT_API_BASE_PATH || '/';
  let url = `${API_ORIGIN_FINAL}${base}api/${safePath}`;
  // Removed db parameter appending to avoid WAF 400 errors
  return url;
}

function bodyHasBinaryValue(body) {
  if (!body || typeof body !== 'object') return false;
  return Object.values(body).some((v) => {
    if (typeof File !== 'undefined' && v instanceof File) return true;
    if (typeof Blob !== 'undefined' && v instanceof Blob) return true;
    if (Array.isArray(v)) {
      return v.some((item) => (
        (typeof File !== 'undefined' && item instanceof File) ||
        (typeof Blob !== 'undefined' && item instanceof Blob)
      ));
    }
    return false;
  });
}

function createRequestBody(body, finalHeaders = {}, { forceJson = false } = {}) {
  const hasBody = body !== undefined && body !== null;
  let finalBody;

  if (hasBody) {
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      finalBody = body;
    } else if (typeof body === 'string') {
      if (!finalHeaders['Content-Type']) {
        finalHeaders['Content-Type'] = 'application/json';
      }
      finalBody = body;
    } else if (typeof body === 'object') {
      // Para cuerpos sin archivos reales (fotos/firmas van como base64 dentro
      // de un campo de texto, no como File), enviar JSON crudo en vez de
      // multipart/form-data cuando se pide explícitamente (forceJson): evita
      // por completo la codificación multipart para campos JSON grandes con
      // datos anidados (ej. sections_json con varias fotos), que en algunos
      // WebView de Android terminaba perdiendo ese campo específico sin
      // ningún error visible, aunque el resto de campos sí llegaban bien.
      if (forceJson && !bodyHasBinaryValue(body)) {
        if (!finalHeaders['Content-Type']) {
          finalHeaders['Content-Type'] = 'application/json';
        }
        finalBody = JSON.stringify(body);
      } else {
        finalBody = new FormData();
        for (const [k, v] of Object.entries(body)) {
          if (v === undefined || v === null) continue;
          if (v instanceof File) {
            finalBody.append(k, v);
          } else if (Array.isArray(v)) {
            for (const item of v) {
              if (item === undefined || item === null) continue;
              if (typeof item === 'object') {
                finalBody.append(`${k}[]`, JSON.stringify(item));
              } else {
                finalBody.append(`${k}[]`, String(item));
              }
            }
          } else if (typeof v === 'object') {
            finalBody.append(k, JSON.stringify(v));
          } else {
            finalBody.append(k, String(v));
          }
        }
      }
    }
  }

  return finalBody;
}

async function performNetworkRequest({ path, url, method, headers, body, signal, cache = 'no-store' }) {
  const res = await fetch(url, {
    method,
    headers,
    body,
    signal,
    cache,
    credentials: 'include',
  });

  const clone = res.clone();
  let json = null;
  let text = null;
  try { json = await res.json(); } catch (_) {
    try { text = await clone.text(); } catch (_) { text = null; }
  }

  if (!res.ok) {
    reportSigDebug('C', 'HTTP error en request API', {
      path,
      url,
      status: res.status,
      statusText: res.statusText || '',
    });
    const msg = (json && typeof json === 'object' && json.error) ? json.error : ((text && String(text).trim()) ? String(text).trim().slice(0, 200) : (res.statusText || 'Error HTTP'));
    throw new Error(`HTTP ${res.status}: ${msg}`);
  }

  if (!json || typeof json !== 'object') {
    const snippet = (text && String(text).trim()) ? String(text).trim().slice(0, 240) : 'Respuesta no JSON';
    throw new Error(`Respuesta inválida del servidor (${res.status}): ${snippet}`);
  }

  if (json && json.ok === false) {
    reportSigDebug('D', 'API respondio ok=false', {
      path,
      url,
      error: json.error || '',
    });
    throw new Error(json.error || 'Error API');
  }

  reportSigDebug('E', 'Request API exitosa', {
    path,
    url,
    ok: true,
    keys: Object.keys(json || {}).slice(0, 10),
  });
  return json;
}

async function flushSigMobileOfflineQueue() {
  if (!shouldUseSigMobileOffline()) {
    return { ok: false, synced: 0, pending: 0, message: 'Sincronización disponible solo en SIG mobile.' };
  }
  if (isRuntimeOffline()) {
    return { ok: false, synced: 0, pending: getOfflineQueue().length, message: 'Sin conexión a internet.' };
  }
  await ensureApiBaseValidated();
  const queue = getOfflineQueue();
  if (!queue.length) {
    return { ok: true, synced: 0, pending: 0, message: 'No hay registros pendientes por sincronizar.' };
  }

  let synced = 0;
  const remaining = [];
  for (const item of queue) {
    const finalHeaders = {};
    const auth = buildAuthHeaderValue();
    if (auth) finalHeaders.Authorization = auth;
    const requestBody = createRequestBody(item.body, finalHeaders, { forceJson: true });
    try {
      await performNetworkRequest({
        path: item.path,
        url: buildApiUrl(item.path),
        method: String(item.method || 'POST').toUpperCase(),
        headers: finalHeaders,
        body: requestBody,
        cache: 'no-store',
      });
      synced += 1;
    } catch (error) {
      remaining.push(item);
      if (/NetworkError|Failed to fetch|fetch/i.test(String(error?.message || ''))) {
        remaining.push(...queue.slice(queue.indexOf(item) + 1));
        break;
      }
    }
  }

  setOfflineQueue(remaining);
  return {
    ok: remaining.length === 0,
    synced,
    pending: remaining.length,
    message: remaining.length === 0
      ? `Sincronización completada. ${synced} registro(s) enviado(s).`
      : `Se sincronizaron ${synced} registro(s). Quedan ${remaining.length} pendiente(s).`,
  };
}

/**
 * Convierte objeto a query string
 * @param {Object} params - Parámetros
 * @returns {string} Query string
 */
function toQuery(params = {}) {
  const entries = Object.entries(params).filter(([_, v]) => 
    v !== undefined && v !== null && v !== ''
  );
  if (!entries.length) return '';
  const qs = new URLSearchParams();
  for (const [k, v] of entries) qs.append(k, String(v));
  return `?${qs.toString()}`;
}

/**
 * Realiza request HTTP a la API
 * @param {string} path - Endpoint
 * @param {Object} options - Opciones de fetch
 * @returns {Promise<Object>} Respuesta JSON
 */
async function request(path, { method = 'GET', body, headers = {}, signal, cache = 'no-store', forceJson: explicitForceJson = false } = {}) {
  await ensureApiBaseValidated();
  const url = buildApiUrl(path);
  // #region debug-point B:request-start
  reportSigDebug('B', 'Inicio de request API', {
    path,
    method: String(method || 'GET').toUpperCase(),
    url,
    apiOriginFinal: API_ORIGIN_FINAL,
    currentApiBasePath: CURRENT_API_BASE_PATH,
    isNative,
    appVariant: APP_VARIANT,
  });
  // #endregion
  // Bloquear escrituras si está habilitado el flag
  const upperMethod = String(method || 'GET').toUpperCase();
  const offlineCapable = shouldUseSigMobileOffline();
  const offlineNow = offlineCapable && (isOfflineNative || isRuntimeOffline());
  const shouldQueueMutation = shouldQueueSigMobileMutation(path, upperMethod);
  if (DISABLE_WRITES && upperMethod !== 'GET') {
    const err = new Error('Operaciones de escritura deshabilitadas en desarrollo');
    console.warn('[api] blocked write:', upperMethod, url);
    throw err;
  }
  if (offlineNow) {
    if (upperMethod === 'GET') {
      return getOfflineGetFallback(path);
    } else if (!shouldQueueMutation) {
      const err = new Error('Sin conexión a internet.');
      err.code = 'SIG_MOBILE_OFFLINE_UNAVAILABLE';
      throw err;
    }
    // shouldQueueMutation === true: no se corta acá. navigator.onLine (base
    // de offlineNow) puede estar equivocado en ciertos dispositivos/WebViews
    // (reporta "sin conexión" con internet funcionando), y antes esto
    // encolaba directo en localStorage sin más alternativa — dejando el
    // guardado a merced de que esa escritura funcionara, sin intentar nunca
    // la red real. Ahora se deja caer al intento de red de abajo, igual que
    // en el caso "online"; solo se encola como respaldo en el catch si la
    // petición de red falla de verdad.
  }

  const finalHeaders = { ...headers };
  if (!('Authorization' in finalHeaders) && !('authorization' in finalHeaders)) {
    const auth = buildAuthHeaderValue();
    if (auth) finalHeaders.Authorization = auth;
  }
  if (currentPageContext && !('X-Senerfi-Page' in finalHeaders)) {
    finalHeaders['X-Senerfi-Page'] = currentPageContext;
  }
  const finalBody = createRequestBody(body, finalHeaders, { forceJson: shouldQueueMutation || explicitForceJson });

  try {
    const json = await performNetworkRequest({
      path,
      url,
      method: upperMethod,
      headers: finalHeaders,
      body: finalBody,
      signal,
      cache,
    });
    if (offlineCapable && upperMethod === 'GET') {
      saveOfflineCacheEntry(path, json);
      return applyOfflineQueueToCachedResponse(path, json);
    }
    return json;
  } catch (error) {
    // #region debug-point F:request-catch
    reportSigDebug('F', 'Excepcion en request API', {
      path,
      url,
      message: error?.message || String(error || ''),
    });
    // #endregion
    if (offlineCapable && upperMethod === 'GET') {
      const cached = getOfflineCacheEntry(path);
      if (cached && typeof cached === 'object') {
        return applyOfflineQueueToCachedResponse(path, cached);
      }
    }
    if (shouldQueueMutation && /NetworkError|Failed to fetch|fetch|offline/i.test(String(error?.message || ''))) {
      const tempId = await enqueueOfflineMutation({ path, method: upperMethod, body });
      return buildOfflineMutationResponse(path, tempId, serializeQueueBody(body));
    }
    console.error(`Error en request a ${url}:`, error);
    throw error;
  }
}

// Archivos que exigen sesión (p. ej. documentos de preoperacionales: orden
// de trabajo, aptitud física). No sirven como <a href> directo porque la
// sesión va en el encabezado Authorization; se descargan y se abren como blob.
// token: token de conductor (APK); sin él se usa la sesión NOVA.
export async function fetchProtectedFile(url, { token } = {}) {
  const headers = {};
  const auth = token ? `Bearer ${token}` : buildAuthHeaderValue();
  if (auth) headers.Authorization = auth;
  const res = await fetch(url, { headers, credentials: 'include', cache: 'no-store' });
  if (!res.ok) throw new Error(res.status === 404 ? 'El documento ya no está disponible.' : 'No fue posible abrir el documento.');
  return res.blob();
}

export async function openProtectedFile(url, opts) {
  // La ventana se abre antes del fetch para que el navegador no la bloquee.
  const win = window.open('', '_blank');
  try {
    const blob = await fetchProtectedFile(url, opts);
    const objectUrl = URL.createObjectURL(blob);
    if (win) win.location.href = objectUrl; else window.location.href = objectUrl;
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  } catch (e) {
    if (win) win.close();
    throw e;
  }
}

/**
 * API client - Todas las operaciones disponibles
 */
export const api = {
  // Diagnóstico
  diag: {
    ping: () => request('diag.php'),
  },
  sync: {
    flushPending: () => flushSigMobileOfflineQueue(),
    getPendingCount: () => getOfflineQueue().length,
    isOffline: () => shouldUseSigMobileOffline() && (isOfflineNative || isRuntimeOffline()),
  },

  // Autenticación
  auth: {
    login: (payload) => request('login.php', {
      method: 'POST',
      body: {
        ...(payload || {}),
        app_variant: APP_VARIANT || undefined,
      },
    }),
    verify2fa: (payload) => request('verify_2fa.php', { method: 'POST', body: payload }),
    resend2fa: (payload) => request('resend_2fa.php', { method: 'POST', body: payload }),
    totp: {
      status: () => request('totp_setup.php', { method: 'POST', body: { action: 'status' } }),
      start: () => request('totp_setup.php', { method: 'POST', body: { action: 'start' } }),
      confirm: (code) => request('totp_setup.php', { method: 'POST', body: { action: 'confirm', code } }),
      disable: (password) => request('totp_setup.php', { method: 'POST', body: { action: 'disable', password } }),
    },
    // Activación obligatoria de TOTP en el momento del login, antes de tener
    // sesión (ver server/api/totp_enroll.php).
    totpEnroll: {
      start: ({ user_id, enrollment_token }) => request('totp_enroll.php', {
        method: 'POST',
        body: { action: 'start', user_id, enrollment_token },
      }),
      confirm: ({ user_id, enrollment_token, code }) => request('totp_enroll.php', {
        method: 'POST',
        body: { action: 'confirm', user_id, enrollment_token, code },
      }),
    },
    // Aprobación de activaciones de 2FA pendientes (solo rol Administrador).
    totpAdmin: {
      list: () => request('totp_admin.php', { method: 'POST', body: { action: 'list' } }),
      approve: (userId) => request('totp_admin.php', { method: 'POST', body: { action: 'approve', user_id: userId } }),
      reject: (userId) => request('totp_admin.php', { method: 'POST', body: { action: 'reject', user_id: userId } }),
      search: (q) => request('totp_admin.php', { method: 'POST', body: { action: 'search', q } }),
      reset: (userId) => request('totp_admin.php', { method: 'POST', body: { action: 'reset', user_id: userId } }),
    },
  },

  // Instalación
  install: {
    run: () => request('install.php'),
  },

  // PERSONAS/COLABORADORES - Endpoint principal
  people: {
    /**
     * Obtener colaboradores
     * @param {Object} filters - { id, dni, q, limit, offset, order, clean, include_vehicle_like }
     * Por defecto excluye filas con forma de placa (vehículos mal registrados en people).
     */
    get: (filters = {}) => {
      const f = { ...filters };
      if (String(f.include_vehicle_like) !== '1') {
        f.exclude_vehicle_like = 1;
      }
      return request('people.php' + toQuery(f));
    },
    
    /**
     * Crear o actualizar colaborador
     * @param {FormData|Object} payload - Datos del colaborador
     */
    upsert: (payload) => request('people.php', { 
      method: 'POST', 
      body: payload 
    }),

    activate: (payload) => request('people.php', {
      method: 'POST',
      body: { ...(payload || {}), action: 'activate' },
    }),

    employmentHistory: (filters = {}) => request('people.php' + toQuery({ action: 'employment_history', ...filters })),

    uploadSignature: (payload) => request('people.php', {
      method: 'POST',
      body: { ...(payload || {}), action: 'upload_signature' },
    }),

    /** Catálogo de perfiles operativos (display_name en tabla roles; no es RBAC de admin_users). */
    operationalRoleCatalog: () => request('people.php' + toQuery({ action: 'operational_role_catalog' })),
  },

  // Transportes
  hrDotaciones: {
      get: (filters = {}) => request('hr_dotaciones.php' + toQuery(filters)),
      upsert: (payload) => request('hr_dotaciones.php', { method: 'POST', body: payload }),
      delete: (id) => request('hr_dotaciones.php', { method: 'POST', body: { id, action: 'delete' } }),
    },
  vehicles: {
      get: (filters = {}) => request('vehicles.php' + toQuery(filters)),
      upsert: (payload) => request('vehicles.php', { method: 'POST', body: payload }),
      delete: (payload) => request('vehicles.php', { method: 'POST', body: { ...payload, action: 'delete' } }),
      setOilChange: (payload) => request('vehicles.php', { method: 'POST', body: { ...payload, action: 'set_oil_change' } }),
      setBrakeChange: (payload) => request('vehicles.php', { method: 'POST', body: { ...payload, action: 'set_brake_change' } }),
    },
    preoperationals: {
      get: (filters = {}) => request('preoperationals.php' + toQuery(filters)),
      upsert: (payload) => request('preoperationals.php', { method: 'POST', body: payload }),
      range: (filters = {}) => request('preoperationals.php' + toQuery({ action: 'range', ...filters })),
      latestMileage: (filters = {}) => request('preoperationals.php' + toQuery({ action: 'latest_mileage', ...filters })),
      updateMileage: (payload) => request('preoperationals.php', { method: 'POST', body: { ...payload, action: 'update_mileage' } }),
      tankings: (filters = {}) => request('preoperationals.php' + toQuery({ action: 'tankings', ...filters })),
      tanqueoUpsert: (payload) => request('preoperationals.php', { method: 'POST', body: { ...payload, action: 'tanqueo_upsert' }, forceJson: true }),
      pendingAuthorization: (filters = {}) => request('preoperationals.php' + toQuery({ action: 'pending_authorization', ...filters })),
      signAuthorization: (payload) => request('preoperationals.php', { method: 'POST', body: { ...payload, action: 'sign_authorization' } }),
      uploadPdf: (payload) => request('preoperationals.php', { method: 'POST', body: { ...payload, action: 'upload_pdf' } }),
      // decision: 'aprobado' | 'bloqueado' (notes obligatorio al bloquear)
      approve: (payload) => request('preoperationals.php', { method: 'POST', body: { ...payload, action: 'approve' } }),
    },
    pesv: {
      list: (filters = {}) => request('pesv.php' + toQuery({ action: 'list', ...filters })),
      catalog: (filters = {}) => request('pesv.php' + toQuery({ action: 'catalog', ...filters })),
      upsert: (payload) => {
        if (typeof FormData !== 'undefined' && payload instanceof FormData) {
          if (!payload.has('action')) payload.append('action', 'upsert');
          return request('pesv.php', { method: 'POST', body: payload });
        }
        return request('pesv.php', { method: 'POST', body: { ...(payload || {}), action: 'upsert' } });
      },
      delete: (payload) => request('pesv.php', { method: 'POST', body: { ...(payload || {}), action: 'delete' } }),
    },
    vehicleDotacion: {
      /** vehicle_id (preferido) o plate — lista dotación en assignments del vehículo */
      get: (filters = {}) => request('vehicle_dotacion.php' + toQuery(filters)),
    },

  // Movimientos
  movements: {
    get: (filters = {}) => request('movements.php' + toQuery(filters)),
    create: (payload) => request('movements.php', { method: 'POST', body: payload }),
    entryBatches: (filters = {}) => request('movements.php' + toQuery({ ...filters, action: 'entry_batches' })),
  },

  // Control Operativo - Registros por grupo (línea viva, proyectos, grúas, alumbrado público)
  coRecords: {
    list: (group) => request('co_records.php' + toQuery({ group })),
    upsert: (payload) => request('co_records.php', { method: 'POST', body: payload }),
    delete: (group, id) => request('co_records.php', { method: 'POST', body: { action: 'delete', group, id } }),
  },
  coLineaViva: {
    list: () => request('co_linea_viva.php'),
    upsert: (payload) => request('co_linea_viva.php', { method: 'POST', body: payload }),
    delete: (id) => request('co_linea_viva.php', { method: 'POST', body: { action: 'delete', id } }),
  },
  coProyectos: {
    list: () => request('co_proyectos.php'),
    capabilities: () => request('co_proyectos.php?capabilities=1'),
    upsert: (payload) => request('co_proyectos.php', { method: 'POST', body: payload }),
    delete: (id) => request('co_proyectos.php', { method: 'POST', body: { action: 'delete', id } }),
  },
  coGruas: {
    list: () => request('co_gruas.php'),
    upsert: (payload) => request('co_gruas.php', { method: 'POST', body: payload }),
    delete: (id) => request('co_gruas.php', { method: 'POST', body: { action: 'delete', id } }),
  },
  coAlumbrado: {
    list: () => request('co_alumbrado.php'),
    upsert: (payload) => request('co_alumbrado.php', { method: 'POST', body: payload }),
    delete: (id) => request('co_alumbrado.php', { method: 'POST', body: { action: 'delete', id } }),
  },
  alumbradoReportes: {
    list: () => request('alumbrado_reportes.php'),
    upsert: (payload) => request('alumbrado_reportes.php', { method: 'POST', body: payload }),
    delete: (id) => request('alumbrado_reportes.php', { method: 'POST', body: { action: 'delete', id } }),
  },
  alumbradoMatInstalado: {
    list: () => request('alumbrado_mat_instalado.php'),
    upsert: (payload) => request('alumbrado_mat_instalado.php', { method: 'POST', body: payload }),
    delete: (id) => request('alumbrado_mat_instalado.php', { method: 'POST', body: { action: 'delete', id } }),
  },
  alumbradoValidacion: {
    get: (codigo) => request('alumbrado_validacion.php' + toQuery({ codigo })),
  },
  alumbradoExporte: {
    get: (filters = {}) => request('alumbrado_exporte.php' + toQuery(filters)),
  },
  // Evidencias (fotos)
  evidence: {
    upload: (payload) => request('upload_evidence.php', { method: 'POST', body: payload }),
    list: (filters = {}) => request('list_evidence.php' + toQuery(filters)),
  },

  // RRHH adicional
  hr: {
    employees: {
      list: (filters = {}) => request('hr_employees.php' + toQuery(filters)),
      get: (filters = {}) => request('hr_employees.php' + toQuery(filters)),
      upsert: (payload) => request('hr_employees.php', { method: 'POST', body: payload }),
    },
    files: {
      upload: (payload) => request('upload_hr.php', { method: 'POST', body: payload }),
      list: (filters = {}) => request('list_hr.php' + toQuery(filters)),
    },
    profiles: {
      list: (filters = {}) => request('hr_profiles.php' + toQuery(filters)),
      get: (filters = {}) => request('hr_profiles.php' + toQuery(filters)),
      upsert: (payload) => request('hr_profiles.php', { method: 'POST', body: payload }),
    },
    vacations: {
      list: (filters = {}) => request('hr_vacations.php' + toQuery({ action: 'list', ...filters })),
      upsert: (payload) => request('hr_vacations.php', { method: 'POST', body: { ...payload, action: 'upsert' } }),
      delete: (id) => request('hr_vacations.php', { method: 'POST', body: { action: 'delete', id } }),
    },
    attendance: {
      list: (filters = {}) => request('hr_attendance.php' + toQuery(filters)),
      getDay: (filters = {}) => request('hr_attendance.php' + toQuery(filters)),
      upsert: (payload) => request('hr_attendance.php', { method: 'POST', body: payload }),
    },
  },

  // Comunicados y Campañas
  comunicados: {
    get: (filters = {}) => request('comunicados.php' + toQuery(filters)),
    upsert: (payload) => request('comunicados.php', {
      method: 'POST',
      body: payload,
    }),
    delete: (payload) => request('comunicados.php', {
      method: 'POST',
      body: { ...payload, action: 'delete' },
    }),
  },

  // Almacén
  brandItems: {
    get: (filters = {}) => request('brand_items.php' + toQuery(filters)),
    upsert: (payload) => request('brand_items.php', { method: 'POST', body: payload }),
    delete: (payload) => request('brand_items.php', { method: 'POST', body: { ...payload, action: 'delete' } }),
  },

  stockByWarehouse: {
    get: (filters = {}) => request('stock_by_warehouse.php' + toQuery(filters)),
    upsert: (payload) => request('stock_by_warehouse.php', { method: 'POST', body: payload }),
    delete: (payload) => request('stock_by_warehouse.php', { method: 'POST', body: { ...payload, action: 'delete' } }),
  },

  // Control Operativo
  operational: {
    listBrigades: (filters = {}) => request('operational.php' + toQuery({ action: 'list_brigades', ...filters })),
    saveBrigade: (payload) => request('operational.php?action=save_brigade', {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json' }
    }),
    listOrders: (filters = {}) => request('operational.php' + toQuery({ action: 'list_orders', ...filters })),
    saveOrder: (payload) => request('operational.php?action=save_order', {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json' }
    }),
    deleteOrder: (payload) => request('operational.php?action=delete_order', {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json' }
    }),
    getConfig: (filters = {}) => request('operational.php' + toQuery({ action: 'get_config', ...filters })),
    saveConfig: (payload) => request('operational.php?action=save_config', {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json' }
    }),
    deleteBrigade: (payload) => request('operational.php?action=delete_brigade', {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json' }
    }),
  },


  // Seriales disponibles por material/bodega
  serials: {
    list: (filters = {}) => request('serials.php' + toQuery(filters)),
    upsert: (payload) => request('serials.php', { method: 'POST', body: payload }),
  },

  assignments: {
    get: (filters = {}) => request('assignments.php' + toQuery(filters)),
    create: (payload) => request('assignments.php', { method: 'POST', body: payload }),
    update: (payload) => request('assignments.php', { method: 'POST', body: { ...payload, action: 'update' } }),
    delete: (payload) => request('assignments.php', { method: 'POST', body: { ...payload, action: 'delete' } }),
  },

  warehouses: {
    list: (filters = {}) => request('warehouses.php' + toQuery(filters)),
    upsert: (payload) => request('warehouses.php', { method: 'POST', body: payload }),
    delete: (payload) => request('warehouses.php', { method: 'POST', body: { ...payload, action: 'delete' } }),
  },

  // SGI
  sgi: {
    requests: {
      list: (filters = {}) => request('sgi_requests.php' + toQuery(filters)),
      upsert: (payload) => request('sgi_requests.php', { method: 'POST', body: payload }),
      delete: (payload) => request('sgi_requests.php', { method: 'POST', body: { ...payload, action: 'delete' } }),
    },
    basePodas: {
      list: (filters = {}) => request('sgi_base_podas.php' + toQuery(filters)),
      upsert: (payload) => request('sgi_base_podas.php', { method: 'POST', body: payload }),
      delete: (payload) => request('sgi_base_podas.php', { method: 'POST', body: { ...payload, action: 'delete' } }),
    },
    inspections: {
      list: (filters = {}) => request('sgi_environmental_inspections.php' + toQuery(filters)),
      upsert: (payload) => request('sgi_environmental_inspections.php', { method: 'POST', body: payload }),
      delete: (payload) => request('sgi_environmental_inspections.php', { method: 'POST', body: { ...payload, action: 'delete' } }),
    },
    sstInspections: {
      list: (filters = {}) => request('sgi_sst_inspections.php' + toQuery(filters)),
      upsert: (payload) => request('sgi_sst_inspections.php', { method: 'POST', body: payload }),
      delete: (payload) => request('sgi_sst_inspections.php', { method: 'POST', body: { ...payload, action: 'delete' } }),
    },
    documentation: {
      list: (filters = {}) => request('sgi_documentation_forms.php' + toQuery(filters)),
      upsert: (payload) => request('sgi_documentation_forms.php', { method: 'POST', body: payload }),
      delete: (payload) => request('sgi_documentation_forms.php', { method: 'POST', body: { ...payload, action: 'delete' } }),
    },
    waste: {
      list: (filters = {}) => request('sgi_waste_management.php' + toQuery(filters)),
      upsert: (payload) => request('sgi_waste_management.php', { method: 'POST', body: payload }),
      delete: (payload) => request('sgi_waste_management.php', { method: 'POST', body: { ...payload, action: 'delete' } }),
    },
    services: {
      list: (filters = {}) => request('sgi_service_control.php' + toQuery(filters)),
      upsert: (payload) => request('sgi_service_control.php', { method: 'POST', body: payload }),
      delete: (payload) => request('sgi_service_control.php', { method: 'POST', body: { ...payload, action: 'delete' } }),
    },
    species: {
      list: (filters = {}) => request('sgi_species.php' + toQuery(filters)),
      upsert: (payload) => request('sgi_species.php', { method: 'POST', body: payload }),
      delete: (payload) => request('sgi_species.php', { method: 'POST', body: { ...payload, action: 'delete' } }),
    },
    accidentality: {
      list: (filters = {}) => request('sgi_people_tracking.php' + toQuery({ ...filters, report_kind: 'accidentalidad' })),
      upsert: (payload) => request('sgi_people_tracking.php', { method: 'POST', body: { ...(payload || {}), report_kind: 'accidentalidad' } }),
      delete: (payload) => request('sgi_people_tracking.php', { method: 'POST', body: { ...(payload || {}), action: 'delete', report_kind: 'accidentalidad' } }),
    },
    absenteeism: {
      list: (filters = {}) => request('sgi_people_tracking.php' + toQuery({ ...filters, report_kind: 'ausentismo' })),
      upsert: (payload) => request('sgi_people_tracking.php', { method: 'POST', body: { ...(payload || {}), report_kind: 'ausentismo' } }),
      delete: (payload) => request('sgi_people_tracking.php', { method: 'POST', body: { ...(payload || {}), action: 'delete', report_kind: 'ausentismo' } }),
    },
  },

  // Contabilidad
  accounting: {
    providers: {
      list: (filters = {}) => request('providers.php' + toQuery(filters)),
      upsert: (payload) => request('providers.php', { method: 'POST', body: payload }),
      delete: (payload) => request('providers.php', { method: 'POST', body: { ...payload, action: 'delete' } }),
      bulkUpsert: (payload) => request('providers.php', { method: 'POST', body: { ...(payload || {}), action: 'bulk_upsert' } }),
    },
    costCenters: {
      list: (filters = {}) => request('cost_centers.php' + toQuery(filters)),
      upsert: (payload) => request('cost_centers.php', { method: 'POST', body: payload }),
      delete: (payload) => request('cost_centers.php', { method: 'POST', body: { ...payload, action: 'delete' } }),
    },
    expenseRequests: {
      list: (filters = {}) => request('expense_requests.php' + toQuery(filters)),
      get: (filters = {}) => request('expense_requests.php' + toQuery({ action: 'get', ...filters })),
      upsert: (payload) => request('expense_requests.php', { method: 'POST', body: payload }),
      validate: (payload) => request('expense_requests.php', { method: 'POST', body: { ...payload, action: 'validate' } }),
      approve: (payload) => request('expense_requests.php', { method: 'POST', body: { ...payload, action: 'approve' } }),
      reject: (payload) => request('expense_requests.php', { method: 'POST', body: { ...payload, action: 'reject' } }),
      delete: (payload) => request('expense_requests.php', { method: 'POST', body: { ...payload, action: 'delete' } }),
      myPending: () => request('expense_requests.php' + toQuery({ action: 'my_pending' })),
    },
    purchaseRequests: {
      list: (filters = {}) => request('purchase_requests.php' + toQuery(filters)),
      nextCode: (filters = {}) => request('purchase_requests.php' + toQuery({ action: 'next_code', ...filters })),
      upsert: (payload) => request('purchase_requests.php', { method: 'POST', body: payload }),
      delete: (payload) => request('purchase_requests.php', { method: 'POST', body: { ...payload, action: 'delete' } }),
      listRequesters: (filters = {}) => request('purchase_requests.php' + toQuery({ action: 'list_requesters', ...filters })),
      myPending: () => request('purchase_requests.php' + toQuery({ action: 'my_pending' })),
    },
    purchaseOrders: {
      list: (filters = {}) => request('purchase_requests.php' + toQuery({ action: 'list_orders', ...filters })),
      nextCode: (filters = {}) => request('purchase_requests.php' + toQuery({ action: 'next_oc_code', ...filters })),
      emit: (payload) => request('purchase_requests.php', { method: 'POST', body: { ...payload, action: 'emit_oc' } }),
      createDirect: (payload) => request('purchase_requests.php', { method: 'POST', body: { ...payload, action: 'create_oc_direct' } }),
      update: (payload) => request('purchase_requests.php', { method: 'POST', body: { ...payload, action: 'update_oc' } }),
      delete: (payload) => request('purchase_requests.php', { method: 'POST', body: { ...payload, action: 'delete_oc' } }),
      approve: (payload) => request('purchase_requests.php', { method: 'POST', body: { ...payload, action: 'approve_oc' } }),
      reject: (payload) => request('purchase_requests.php', { method: 'POST', body: { ...payload, action: 'reject_oc' } }),
      uploadPdf: (payload) => request('purchase_requests.php', { method: 'POST', body: { ...payload, action: 'upload_oc_pdf' } }),
      updateStatus: (payload) => request('purchase_requests.php', { method: 'POST', body: { ...payload, action: 'update_oc_status' } }),
      updatePaymentStatus: (payload) => request('purchase_requests.php', { method: 'POST', body: { ...payload, action: 'update_oc_payment_status' } }),
      receive: (payload) => request('purchase_requests.php', { method: 'POST', body: { ...payload, action: 'receive_oc' } }),
    },
    overtimeExtras: {
      list: (filters = {}) => request('overtime_extras.php' + toQuery(filters)),
      get: (filters = {}) => request('overtime_extras.php' + toQuery({ action: 'get', ...filters })),
      stats: (filters = {}) => request('overtime_extras.php' + toQuery({ action: 'stats', ...filters })),
      noveltiesList: (filters = {}) => request('overtime_extras.php' + toQuery({ action: 'novelty_list', ...filters })),
      noveltiesGet: (filters = {}) => request('overtime_extras.php' + toQuery({ action: 'novelty_get', ...filters })),
      noveltiesBalance: (filters = {}) => request('overtime_extras.php' + toQuery({ action: 'novelty_balance', ...filters })),
      upsert: (payload) => request('overtime_extras.php', { method: 'POST', body: payload }),
      noveltiesUpsert: (payload) => request('overtime_extras.php', { method: 'POST', body: { ...payload, action: 'novelty_upsert' } }),
      noveltiesDelete: (payload) => request('overtime_extras.php', { method: 'POST', body: { ...payload, action: 'novelty_delete' } }),
      noveltyApprove: (payload) => request('overtime_extras.php', { method: 'POST', body: { ...payload, action: 'novelty_approve' } }),
      noveltyBossReject: (payload) => request('overtime_extras.php', { method: 'POST', body: { ...payload, action: 'novelty_boss_reject' } }),
      noveltyHrReceive: (payload) => request('overtime_extras.php', { method: 'POST', body: { ...payload, action: 'novelty_hr_receive' } }),
      noveltyHrReject: (payload) => request('overtime_extras.php', { method: 'POST', body: { ...payload, action: 'novelty_hr_reject' } }),
      noveltyHrComment: (payload) => request('overtime_extras.php', { method: 'POST', body: { ...payload, action: 'novelty_hr_comment' } }),
      // FT-GTH-022 (oct-2026)
      noveltyTypes: () => request('overtime_extras.php' + toQuery({ action: 'novelty_types' })),
      noveltyProfile: () => request('overtime_extras.php' + toQuery({ action: 'novelty_profile' })),
      noveltyAttachmentGet: (filters = {}) => request('overtime_extras.php' + toQuery({ action: 'novelty_attachment_download', ...filters })),
      noveltyBossReturn: (payload) => request('overtime_extras.php', { method: 'POST', body: { ...payload, action: 'novelty_boss_return' }, forceJson: true }),
      noveltyDirectorDecide: (payload) => request('overtime_extras.php', { method: 'POST', body: { ...payload, action: 'novelty_director_decide' }, forceJson: true }),
      noveltyCancel: (payload) => request('overtime_extras.php', { method: 'POST', body: { ...payload, action: 'novelty_cancel' }, forceJson: true }),
      noveltyPdfData: (payload) => request('overtime_extras.php', { method: 'POST', body: { ...payload, action: 'novelty_pdf_data' }, forceJson: true }),
      noveltyAttachmentUpload: (payload) => request('overtime_extras.php', { method: 'POST', body: { ...payload, action: 'novelty_attachment_upload' }, forceJson: true }),
      noveltyTypeConfigSave: (payload) => request('overtime_extras.php', { method: 'POST', body: { ...payload, action: 'novelty_type_config_save' }, forceJson: true }),
      delete: (payload) => request('overtime_extras.php', { method: 'POST', body: { ...payload, action: 'delete' } }),
      validate: (payload) => request('overtime_extras.php', { method: 'POST', body: { ...payload, action: 'validate' } }),
      approve: (payload) => request('overtime_extras.php', { method: 'POST', body: { ...payload, action: 'approve' } }),
      reject: (payload) => request('overtime_extras.php', { method: 'POST', body: { ...payload, action: 'reject' } }),
      myPending: () => request('overtime_extras.php' + toQuery({ action: 'my_pending' })),
      concepts: () => request('overtime_extras.php' + toQuery({ action: 'concepts' })),
      params: () => request('overtime_extras.php' + toQuery({ action: 'params' })),
      calcPreview: (filters = {}) => request('overtime_extras.php' + toQuery({ action: 'calc_preview', ...filters })),
      history: (filters = {}) => request('overtime_extras.php' + toQuery({ action: 'history', ...filters })),
      return: (payload) => request('overtime_extras.php', { method: 'POST', body: { ...payload, action: 'return' } }),
    },
  },

  seguridad: {
    visitors: {
      list: (filters = {}) => request('security_visitors.php' + toQuery(filters)),
      upsert: (payload) => request('security_visitors.php', { method: 'POST', body: payload }),
      updateExit: (payload) => request('security_visitors.php', { method: 'POST', body: { ...payload, action: 'update_exit' } }),
      delete: (payload) => request('security_visitors.php', { method: 'POST', body: { ...payload, action: 'delete' } }),
    },
  },

  // Administración TI
  admin: {
    users: {
      list: (filters = {}) => request('admin_users.php' + toQuery(filters)),
      upsert: (payload) => request('admin_users.php', { method: 'POST', body: payload }),
      delete: (payload) => request('admin_users.php', { method: 'POST', body: { ...payload, action: 'delete' } }),
      setActive: (payload) => request('admin_users.php', { method: 'POST', body: { ...payload, action: 'set_active' } }),
    },
    prefs: {
      get: (filters = {}) => request('admin_prefs.php' + toQuery(filters)),
      upsert: (payload) => request('admin_prefs.php', { method: 'POST', body: payload }),
    },
    roles: {
      list: (filters = {}) => request('roles.php' + toQuery(filters)),
      get: (id) => request('roles.php' + toQuery({ id })),
      upsert: (payload) => request('roles.php', { method: 'POST', body: { ...payload, action: 'upsert' } }),
      setActive: (payload) => request('roles.php', { method: 'POST', body: { ...payload, action: 'set_active' } }),
      setDefaultPermissions: (payload) => request('roles.php', { method: 'POST', body: { ...payload, action: 'set_default_permissions' } }),
      applyTemplate: (payload) => request('roles.php', { method: 'POST', body: { ...payload, action: 'apply_role_template' } }),
    },
    audit: {
      list: (filters = {}) => request('admin_audit.php' + toQuery(filters)),
    },
    operationsLog: {
      list: (filters = {}) => request('admin_operations_log.php' + toQuery(filters)),
      recordDownload: (payload) => request('operations_log_download.php', { method: 'POST', body: payload }),
    },
    supports: {
      list: (filters = {}) => request('support_tickets.php' + toQuery(filters)),
      upsert: (payload) => request('support_tickets.php', { method: 'POST', body: payload }),
      delete: (payload) => request('support_tickets.php', { method: 'POST', body: { ...payload, action: 'delete' } }),
      snapshot: (payload) => request('support_ticket_snapshot.php', { method: 'POST', body: payload }),
      notify: (payload) => request('support_ticket_notify.php', { method: 'POST', body: payload }),
    },
    hvAssets: {
      list: (filters = {}) => request('hv_assets.php' + toQuery(filters)),
      upsert: (payload) => request('hv_assets.php', { method: 'POST', body: payload }),
      delete: (payload) => request('hv_assets.php', { method: 'POST', body: { ...payload, action: 'delete' } }),
    },
    hvInspections: {
      list: (filters = {}) => request('hv_inspections.php' + toQuery({ action: 'list', ...filters })),
      upload: (payload) => request('hv_inspections.php', { method: 'POST', body: payload }),
    },
    bitacoraCambios: {
      list: (filters = {}) => request('bitacora_cambios.php' + toQuery(filters)),
      upsert: (payload) => request('bitacora_cambios.php', { method: 'POST', body: payload }),
      delete: (payload) => request('bitacora_cambios.php', { method: 'POST', body: { ...payload, action: 'delete' } }),
    },
    qualityVersions: {
      list: (filters = {}) => request('quality_versions.php' + toQuery(filters)),
      upsert: (payload) => request('quality_versions.php', { method: 'POST', body: payload }),
      delete: (payload) => request('quality_versions.php', { method: 'POST', body: { ...payload, action: 'delete' } }),
    },
  },

  // Materiales
  materials: {
    get: (filters = {}) => request('materials.php' + toQuery(filters)),
    upsert: (payload) => request('materials.php', { method: 'POST', body: payload }),
    delete: (payload) => request('materials.php', { method: 'POST', body: { ...payload, action: 'delete' } }),
  },

  // Conductores App
  conductores: {
    get: (filters = {}) => request('conductores.php' + toQuery(filters)),
    upsert: (payload) => request('conductores.php', { method: 'POST', body: payload }),
    delete: (payload) => request('conductores.php', { method: 'POST', body: { ...payload, action: 'delete' } }),
  },
  
  brigadas: {
    get: (filters = {}) => request('brigadas.php' + toQuery(filters)),
    upsert: (payload) => request('brigadas.php', { method: 'POST', body: payload }),
    delete: (payload) => request('brigadas.php', { method: 'POST', body: { ...payload, action: 'delete' } }),
  },
  
  // Mantenimientos
  maintenances: {
    get: () => request('maintenances.php'),
    range: (filters = {}) => request('maintenances.php' + toQuery({ action: 'range', ...filters })),
    create: (payload) => {
      if (typeof FormData !== 'undefined' && payload instanceof FormData) {
        if (!payload.has('action')) payload.append('action', 'create');
        return request('maintenances.php', { method: 'POST', body: payload });
      }
      return request('maintenances.php', { method: 'POST', body: { ...payload, action: 'create' } });
    },
    history: (vehicle_id) => request('maintenances.php', { method: 'POST', body: { action: 'history', vehicle_id } }),
    stats: (filters = {}) => request('maintenances.php', { method: 'POST', body: { action: 'stats', ...filters } }),
    update: (payload) => {
      if (typeof FormData !== 'undefined' && payload instanceof FormData) {
        if (!payload.has('action')) payload.append('action', 'update');
        return request('maintenances.php', { method: 'POST', body: payload });
      }
      return request('maintenances.php', { method: 'POST', body: { ...payload, action: 'update' } });
    },
    delete: (payload) => request('maintenances.php', { method: 'POST', body: { ...payload, action: 'delete' } }),
  },
  porteria: {
    board: (filters = {}) => request('porteria.php' + toQuery({ action: 'board', ...filters })),
    vehicle: (filters = {}) => request('porteria.php' + toQuery({ action: 'vehicle', ...filters })),
    registerExit: (payload) => request('porteria.php', { method: 'POST', body: { ...payload, action: 'register_exit' }, forceJson: true }),
    registerEntry: (payload) => request('porteria.php', { method: 'POST', body: { ...payload, action: 'register_entry' }, forceJson: true }),
  },
  maintenanceServiceOrders: {
    nextNumber: () => request('maintenance_service_orders.php?action=next_number'),
    history: (filters = {}) => request('maintenance_service_orders.php' + toQuery({ action: 'history', ...filters })),
    range: (filters = {}) => request('maintenance_service_orders.php' + toQuery({ action: 'range', ...filters })),
    openByVehicle: (vehicle_id) => request('maintenance_service_orders.php' + toQuery({ action: 'open_by_vehicle', vehicle_id })),
    upsert: (payload) => request('maintenance_service_orders.php', { method: 'POST', body: { ...payload, action: 'upsert' } }),
    pendingSeguridad: (filters = {}) => request('maintenance_service_orders.php' + toQuery({ action: 'pending_seguridad', ...filters })),
    authorizeExit: (payload) => request('maintenance_service_orders.php', { method: 'POST', body: { ...payload, action: 'authorize_exit' } }),
  },

  // Asignación de Vehículos
  vehicleAssignments: {
    get: () => request('vehicle_assignments.php'),
    assign: (payload) => request('vehicle_assignments.php', { method: 'POST', body: { ...payload, action: 'assign' } }),
    return: (payload) => request('vehicle_assignments.php', { method: 'POST', body: { ...(payload || {}), action: 'return' } }),
    history: (vehicle_id) => request('vehicle_assignments.php', { method: 'POST', body: { action: 'history', vehicle_id } }),
  },

  technicianStock: {
    get: (identifier) => request('technician_stock.php?identifier=' + encodeURIComponent(identifier)),
  },

  // Sesión de conductor para la app "Preoperacionales" — usa su propio token
  // (conductor_tokens / conductores_app), independiente del login NOVA con 2FA.
  conductorAuth: {
    login: (usuario, password) => request('conductor_preop.php', { method: 'POST', body: { action: 'login', usuario, password } }),
    me: (token) => request('conductor_preop.php?action=me', { headers: { Authorization: `Bearer ${token}` } }),
    logout: (token) => request('conductor_preop.php', { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: { action: 'logout' } }),
  },
  conductorPreop: {
    myVehicle: (token) => request('conductor_preop.php?action=my_vehicle', { headers: { Authorization: `Bearer ${token}` } }),
    today: (token, vehicle_id) => request('conductor_preop.php' + toQuery({ action: 'today', vehicle_id }), { headers: { Authorization: `Bearer ${token}` } }),
    documentUrl: (id) => buildApiUrl('conductor_preop.php' + toQuery({ action: 'document', id })),
    // forceJson: true — estos payloads llevan fotos/firmas como base64 dentro
    // de campos de texto (no como File real). Enviarlos como multipart/form-data
    // (el comportamiento por defecto) perdía campos grandes sin ningún error
    // visible en algunos WebView de Android (mismo problema ya diagnosticado y
    // corregido para SIG Mobile); JSON crudo evita por completo ese riesgo.
    submit: (token, payload) => request('conductor_preop.php', { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: { ...payload, action: 'submit' }, forceJson: true }),
    uploadPdf: (token, payload) => request('conductor_preop.php', { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: { ...payload, action: 'upload_pdf' }, forceJson: true }),
    submitTanqueo: (token, payload) => request('conductor_preop.php', { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: { ...payload, action: 'submit_tanqueo' }, forceJson: true }),
  },
};

/** Asignación/préstamo con destinatario vehículo (movements.php Fase 3). */
export async function registerWarehouseVehicleAssignment(assignmentData) {
  const vehicleId = Number(assignmentData.vehicleId || assignmentData.vehicle_id || 0);
  if (!vehicleId) {
    return { success: false, message: 'Seleccione un vehículo (api.vehicles).' };
  }
  const materialId = parseInt(assignmentData.materialId, 10);
  if (!Number.isFinite(materialId) || materialId <= 0) {
    return { success: false, message: 'Material inválido' };
  }
  const warehouseId = String(assignmentData.warehouseId || '').trim();
  if (!warehouseId) {
    return { success: false, message: 'Bodega requerida' };
  }
  const reqQty = parseInt(assignmentData.quantity, 10);
  if (!Number.isFinite(reqQty) || reqQty <= 0) {
    return { success: false, message: 'Cantidad inválida' };
  }
  const mvType = assignmentData.type || 'Asignación';
  const mvTypeDb = mvType === 'Préstamo' ? 'prestamo' : 'asignacion';
  const serialTrim = String(assignmentData.serial || '').trim();
  const brandResolved = String(assignmentData.brandName || '').trim();

  try {
    const resp = await api.movements.create({
      type: mvTypeDb,
      material_id: materialId,
      warehouse_id: warehouseId,
      vehicle_id: vehicleId,
      assignee_type: 'vehicle',
      serial: serialTrim || undefined,
      brand: brandResolved || undefined,
      quantity: reqQty,
      notes: assignmentData.observations || '',
      requester_name: assignmentData.vehiclePlate || undefined,
    });
    if (resp && resp.ok) {
      return {
        success: true,
        message: 'Asignación al vehículo registrada',
        movementId: resp.movement_id || resp.id || null,
      };
    }
    return { success: false, message: resp?.error || 'No fue posible registrar la asignación al vehículo' };
  } catch (e) {
    return { success: false, message: e?.message || 'Error al registrar asignación al vehículo' };
  }
}
